import type { Ref } from 'vue';
import { useContext } from '@nuxtjs/composition-api';
import {
  helpers,
  required as requiredValidator,
  email as emailValidator,
} from '@vuelidate/validators';
import { checkPublicEmailDomains } from '@webplatform/frontkit/dist/helpers';

const PHONE_RE = /^\+?\d{8,}$/;
const ALPHA_SPACES_RE = /^[\p{L}\s]*$/u;

export default function useValidationRules() {
  const { i18n } = useContext();
  const msg = (key: string) => () => i18n.t(key) as string;

  const withMessage = (key: string, validator: (value: any) => boolean) =>
    helpers.withMessage(msg(key), validator);

  const required = helpers.withMessage(
    msg('validation_required'),
    requiredValidator,
  );

  const checked = withMessage(
    'validation_required',
    (value: boolean) => value === true,
  );

  const email = helpers.withMessage(msg('validation_email'), emailValidator);

  const publicEmail = withMessage(
    'validation_public_email',
    (value: string) => !value || !checkPublicEmailDomains(value),
  );

  const phone = withMessage(
    'validation_phone',
    (value: string) => !value || PHONE_RE.test(value),
  );

  const alphaSpaces = withMessage(
    'validation_alpha_spaces',
    (value: string) => !value || ALPHA_SPACES_RE.test(value),
  );

  const addressRules = <T>(
    suggestions: Ref<T[]>,
    selectedAddress: Ref<string>,
    toText: (item: T) => string,
  ) => ({
    required,
    suggestionsIncludes: withMessage(
      'service_form_valid_address_error',
      (value: string) =>
        !value ||
        suggestions.value.some((item) =>
          toText(item).toLocaleLowerCase().includes(value.toLocaleLowerCase()),
        ),
    ),
    isFullAddress: withMessage(
      'service_form_full_address_error',
      (value: string) =>
        !value ||
        selectedAddress.value.toLocaleLowerCase() === value.toLocaleLowerCase(),
    ),
  });

  const errorMessage = (field: any): string =>
    field?.$errors?.[0]?.$message || '';

  const errorMessages = (field: any): string[] =>
    field?.$errors?.map((error: any) => error.$message) || [];

  return {
    withMessage,
    required,
    checked,
    email,
    publicEmail,
    phone,
    alphaSpaces,
    addressRules,
    errorMessage,
    errorMessages,
  };
}
